/* eslint-disable max-len */
import {
    MutableRefObject,
    useCallback,
    useMemo,
} from 'react'
import {
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import {
    useQueryClient,
} from '@tanstack/react-query'
import Box from '@mui/material/Box'

import {
    SkyNetObjectConfigParams,
    SkyNetObjectTabsOutletContext,
    SkyNetMasterTabConfig,
} from 'app/PoC/DataGridDemoPages/SkyNetObjectRoutes/SkyNetObjectRoutes.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import SkyNetMasterDataGrid from 'app/PoC/DataGridDemoPages/components/SkyNetMasterDataGrid'
import {
    prepareMasterConfig,
} from 'app/PoC/DataGridDemoPages/components/SkyNetObjectProfile/SkyNetObjectProfile.utils'
import useDetailedViewOutlet
    from 'app/PoC/DataGridDemoPages/components/SkyNetObjectProfile/hooks/useDetailedViewOutlet'
import {
    CommonRoutes,
} from 'app/enums/common.enums'
import {
    ColumnConfigMappingType, ExtendedGridApiPro,
} from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid'
import {
    DataGridSupportedFeatures,
} from 'app/PoC/DataGridDemoPages/components/SkyNetMasterDataGrid/SkyNetMasterDataGrid.types'
import {
    SkyNetTableTab,
} from 'app/shared-components/SkyNetTable/types/skyNetTable.types'
import useStyles from 'app/PoC/DataGridDemoPages/components/SkyNetObjectProfile/SkyNetObjectProfile.styles'
import {
    useDataGridSource,
} from 'app/PoC/DataGridDemoPages/components/hooks'
import SkyNetDataGridTabs from '../SkyNetDataGridTabs'
import {
    featuresToHiddenColumns,
} from '../utils/utils'
import useRowSelectionModel from './hooks/useRowSelectionModel'
import useAllowedActionKeys from './hooks/useAllowedActionKeys'

const toolbarControls = []
const defaultFeaturesLists: DataGridSupportedFeatures[] = []
const defaultFiltersUnrelatedToColumns = []

type SkyNetObjectProfileProps = {
    filtersUnrelatedToColumns?: ColumnConfigMappingType[],
    config: SkyNetObjectConfigParams<any>,
    featuresLists?: DataGridSupportedFeatures[],
}

export default function SkyNetObjectProfile({
    filtersUnrelatedToColumns = defaultFiltersUnrelatedToColumns,
    config,
    featuresLists = defaultFeaturesLists,
}: Readonly<SkyNetObjectProfileProps>) {
    const {
        classes,
    } = useStyles()
    const {
        id,
        isDetailsView,
        locationPathname,
        navigate,
        tablePath,
        Outlet,
        activeTabName,
    } = useDetailedViewOutlet()
    const gridApiRef: MutableRefObject<ExtendedGridApiPro> = useGridApiRef<ExtendedGridApiPro>()
    const queryClient = useQueryClient()
    const uniqField = useMemo(() => {
        return config.uniqField || 'id'
    }, [config])

    const additionalColumnsForFilters: ColumnConfigMappingType[] = useMemo(() => {
        const hiddenColumns = featuresToHiddenColumns(featuresLists)

        return [
            ...filtersUnrelatedToColumns,
            ...hiddenColumns,
        ]
    }, [
        filtersUnrelatedToColumns,
        featuresLists,
    ])

    const tableConfig = useMemo(() => {
        return prepareMasterConfig(config)
    }, [config])

    const tableTabsConfig = useMemo(() => {
        return tableConfig.map(({
            name, url,
        }) => {
            return {
                name,
                url,
            } as SkyNetTableTab
        })
    }, [tableConfig])

    const activeTab = useMemo(() => {
        return tableTabsConfig.findIndex((tab) => {
            return tab.url === activeTabName
        })
    }, [
        activeTabName,
        tableTabsConfig,
    ])

    const {
        columns,
        request,
        requestKey,
        actionComponents: tabActionComponents,
    } = useMemo(() => {
        return (tableConfig[activeTab] || {}) as SkyNetMasterTabConfig<any>
    }, [
        activeTab,
        tableConfig,
    ])
    const actionComponents = useMemo(() => {
        return tabActionComponents || config.actionComponents
    }, [
        config.actionComponents,
        tabActionComponents,
    ])

    const allowedActionKeys = useAllowedActionKeys({
        actionComponents,
    })

    const customDataSource = useDataGridSource({
        request,
        key: requestKey,
    })

    const onActiveTabChange = useCallback((newActiveTab: number) => {
        navigate(`${tablePath}/${tableTabsConfig[newActiveTab].url}`)
        customDataSource.invalidate()
    }, [
        customDataSource,
        navigate,
        tablePath,
        tableTabsConfig,
    ])

    /* To refresh data grid from outside */
    const refreshDataGrid = useCallback((refreshRequestKey: RequestKeys) => {
        /* invalidate queries for dataGrid */
        queryClient.invalidateQueries({
            queryKey: [refreshRequestKey],
        })
        gridApiRef.current?.dataSource.fetchRows()
    }, [
        gridApiRef,
        queryClient,
    ])

    const {
        rowSelectionModel, onRowSelectionModelChange,
    } = useRowSelectionModel(id)

    const onRowClick = useCallback(({
        row,
    }) => {
        if (String(row[uniqField]) !== String(id)) {
            navigate(`./${CommonRoutes.Edit}/${row[uniqField]}`)
        } else {
            navigate('..')
        }
    }, [
        id,
        navigate,
        uniqField,
    ])

    /* passing custom props to components rendered in the Outlet */
    const outletContext: SkyNetObjectTabsOutletContext = useMemo(() => {
        return {
            refreshTable: refreshDataGrid,
        }
    }, [refreshDataGrid])

    const tabsComponent = useMemo(() => {
        return (
            <SkyNetDataGridTabs
                tabs={tableTabsConfig}
                isDropDownView={isDetailsView}
                activeTab={activeTab}
                onChange={onActiveTabChange}
            />
        )
    }, [
        activeTab,
        isDetailsView,
        onActiveTabChange,
        tableTabsConfig,
    ])

    return (
        <Box className={classes.container}>
            <>
                <SkyNetMasterDataGrid
                    gridApiRef={gridApiRef}
                    persistentKey={config.persistentKey}
                    columns={columns as unknown as ColumnConfigMappingType[]} // TODO-DK: fix types
                    filtersUnrelatedToColumns={additionalColumnsForFilters}
                    customDataSource={customDataSource}
                    toolbarControls={toolbarControls}
                    tabsComponent={tabsComponent}
                    onRowClick={onRowClick}
                    isDetailsView={isDetailsView}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    toolbarActions={allowedActionKeys}
                />
                <Outlet
                    key={locationPathname}
                    context={outletContext}
                />
            </>
        </Box>
    )
}
